import * as THREE from "three";
import { gsap, Power2 } from "gsap";
import { UIPanels } from "./ui/UIPanels";
import { LibraryWorld } from "./worlds/";
import { AudioEngine } from './audio/core/AudioEngine';
import { LibraryAudioData } from './audio/sceneData/library';
import { LibraryEndAudioData } from './audio/sceneData/libraryEnd';
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { Action, AppEvent, AppView, LibraryView, PanelMode, PoemId, TitleId, worlds } from './constants';

export class WorldHolder {
    /**
     * 
     * @param {canvas} canvas to render on 
     * @param {assets} Assets class holding textures
     * Creates the app which holds the library and worlds
     */
    constructor({ canvas, assets }) {
        this.time = 0;
        this._view = AppView.LIBRARY;
        this.entered = false;
        this.minimalMode = false;
        this._testEnd = false;

        // Loop bindings
        this.loop = this.loop.bind(this);
        this.worldLoop = this.worldLoop.bind(this);
        this.libraryLoop = this.libraryLoop.bind(this);

        // Farm out init functions
        this.initThree(canvas);
        this.createUIPanels(assets);
        this.createLibrary(assets);
        this.initAudio();
        this.initUIPanels();
        this.library.createInitialScreenshots({ renderer: this.renderer });
        this.addMouseListeners();
    }

    /**
     * Creates and initialises the audio engine
     */
    initAudio() {
        // Main Audio Engine
        this.audioEngine = new AudioEngine();

        // Retrieve local storage
        const isMuted = this.audioEngine.loadSavedMuteState();

        // Also ask to load saved voiceover state (no action needed here)
        this.audioEngine.loadVoiceoverState();

        // Update UI mute button
        this.uiPanels.soundBtn.setMuted(isMuted);

        // Listen for changes in the mute state and send to UI
        this.audioEngine.addListener(AppEvent.SOUND_MUTED, this.handleAudioMuteChange.bind(this));

        // Load the library audio first if we aren't
        if (!isMuted) {
            this.loadLibraryAudio({ autoPlay: false });
        }
    }

    handleAudioMuteChange(muted) {
        this.uiPanels.soundBtn.setMuted(muted);

        console.log("WorldHolder handling mute change", muted);

        // If unmuting, initiate audio load for current world
        if (!muted) {
            // Load either library audio or world audio
            const isLibrary = this.view === AppView.LIBRARY;
            const { currentPortal } = this.library;

            this.audioEngine.loadAudioForWorld({
                name: isLibrary ? "library" : currentPortal.name,
                audioData: isLibrary ? LibraryAudioData : currentPortal.audioData,
                initialScene: isLibrary ? 0 : this.currentWorld.currentStep,
                autoPlay: true,
            }); // Always autoplay in this case 
        }
    }

    /**
     * Adds mouse listeners to the document
     */
    addMouseListeners() {
        document.addEventListener("mousedown", () => this.handleMouseDown(), false);
        document.addEventListener("mouseup", () => this.handleMouseUp(), false);
    }

    /**
     * 
     * @param {Assets} assets class
     *  Creates the library world from the supplied assets
     */
    createLibrary(assets) {
        this.library = new LibraryWorld({
            name: "library",
            model: "libraryModel",
            assets,
            uiPanels: this.uiPanels,
        });

        // Play audio if the user interacts with a portal
        this.library.addListener(AppEvent.CLICK_INITIATED, () => this.audioEngine.playGestureAudio());

        // Listen for changes in the library view and send to UI
        this.library.addListener(AppEvent.LIBRARY_VIEW_CHANGE, (view) => this.handleLibraryViewChange(view));

        // Main listener for when the user clicks on a portal
        this.library.addListener(AppEvent.PORTAL_CLICK, () => this.enterCurrentPortal());

        this.currentCamera = this.library.camera;
    }

    handleLibraryViewChange(view) {

        if (this.uiPanels.infoPanel._isOpen) {
            this.uiPanels.closeInfoPanel();
        }

        switch (view) {
            case LibraryView.GLOBAL:
                this.uiPanels.mode = PanelMode.LIBRARY;
                break;
            case LibraryView.FOCUS:
            case LibraryView.IN_PORTAL:
                this.uiPanels.mode = PanelMode.FOCUS;
                break;
        }
    }

    /**
     * 
     * @param {HTMLCanvasElement} canvas
     * Initialises the three renderer and clock
     */
    initThree(canvas) {
        // renderer
        this.renderer = new THREE.WebGLRenderer({
            canvas,
            antialias: true,
            preserveDrawingBuffer: true
        });

        this.renderer.setPixelRatio(this.pixelRatio = window.devicePixelRatio);
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.outputEncoding = THREE.sRGBEncoding; // Deprecated but essential for now
        this.renderer.toneMapping = THREE.LinearToneMapping;
        this.renderer.toneMappingExposure = 1;
        this.renderer.localClippingEnabled = true;

        // Clock
        this.clock = new THREE.Clock();

        // Mouse
        this.mouse = new THREE.Vector2();
    }

    /**
     * 
     * @param {*} assets 
     */
    createUIPanels(assets) {
        this.uiPanels = new UIPanels(assets);
        this.uiPanels.addListener(AppEvent.UI_ACTION, this.handleUIEvent.bind(this));
    }

    initUIPanels() {
        this.uiPanels.model.position.z = -1;

        // Note currentCamera is a getter now - will automatically set to library / world without manually doing it
        this.currentCamera.add(this.uiPanels.model);

        const { titleBrightness, titleDisplace, titleScale } = this.library;

        this.uiPanels.showTitle({
            titleId: TitleId.LIBRARY,
            titleBrightness, titleDisplace, titleScale,
        });
    }

    /**
     * @param {Action} action 
     *  Handles UI events from the UI panels
     */
    handleUIEvent(action) {

        switch (action) {
            case Action.EXPLORE:
                this.audioEngine.playGestureAudio();
                this.enterCurrentPortal();
                break;
            case Action.NEXT:
            case Action.PREVIOUS:
                if (this.view !== AppView.LIBRARY) {
                    this[`${action}Step`](); // same = next | previous + Step
                    this.audioEngine.playGestureAudio();
                }
                break;
            case Action.BACK:
                this.switchToLibrary();
                break;
            case Action.QUIT:
                this.exitCurrentPortal();
                break;
            case Action.SOUND:
                this.audioEngine.toggleMute();
                break;
            default:
                // Catch marker clicks
                const markerTarget = /^marker\_(\w+)/.exec(action);

                if (markerTarget) {
                    const { portalIndex } = worlds.find(w => w.name === markerTarget[1]) || {};

                    if (portalIndex) {
                        this.library.frontCameraStep = portalIndex;
                        this.library.replaceCameraInFrontOfPortal(portalIndex);
                        this.audioEngine.playGestureAudio();
                    }
                }
                break;
        }

        this.audioEngine.playUIEffect(action);

    }


    enterLibrary() { // first time viewer enters the library, just after the onboarding
        this.audioEngine.enterWorld("library");

        this.library.currentStep = 0;
        this.library.updateStep();
        this.uiPanels.hideTitle();

        this.audioEngine.playGestureAudio();

        // a really dirty way to solve the glitch... 
        setTimeout(() => {
            this.setOrbitControls(this.library);
            this.entered = true;
        }, 4000); // can be really short - just needs to be after camera transition onUpdate starts

        this.showPoem(PoemId.LIBRARY);
    }

    focusOrEnterPortal() {
        if (!this.entered) return;

        if (this.uiPanels.infoPanel._isOpen) {
            this.uiPanels.closeInfoPanel();
        }

        if (this.library.view == LibraryView.GLOBAL) {
            this.library.focusOnPortal(this.library.frontCameraStep)
        } else if (this.library.view == LibraryView.FOCUS) {
            this.enterCurrentPortal()
        }
    }

    enterCurrentPortal() {

        if (this.uiPanels.infoPanel._isOpen) {
            this.uiPanels.closeInfoPanel();
        }

        if (this.library.view != LibraryView.FOCUS) {
            return;
        }
        const portalDirection = new THREE.Vector3();

        const { portal3d, we, world } = this.library.currentPortal;
        portal3d.getWorldDirection(portalDirection);

        // portals are sometimes children of other objects, convert their position
        const portalWorldPos = portal3d.localToWorld(new THREE.Vector3());
        const nextPosition = portalWorldPos.clone().addScaledVector(portalDirection, 1.1);

        // Don't want to update from here
        this.uiPanels.mode = PanelMode.TRANSITION;
        this.library.view = LibraryView.IN_PORTAL;

        const stepWorld = world.currentStep;
        const nextTarget = we.localToWorld(world.targets[stepWorld].position.clone());

        const duration = 2;
        const ease = Power2.easeIn;

        gsap.to(this.controls.target, {
            x: nextTarget.x,
            y: nextTarget.y,
            z: nextTarget.z,
            duration,
            ease,
        })

        gsap.to(this.library.camera.position, {
            x: nextPosition.x,
            y: nextPosition.y,
            z: nextPosition.z,
            duration,
            ease,

            onUpdate: () => this.library.camera.lookAt(this.controls.target),
            onComplete: () => this.switchToWorld()
        })

        this.uiPanels.hideMarkers(1);

        world.resetTransform();
    }

    exitCurrentPortal() {

        this.currentWorld.currentStep = 0;
        let portalDirection = new THREE.Vector3();

        const { portal3d } = this.library.currentPortal;

        portal3d.getWorldDirection(portalDirection);

        let portalWorldPos = portal3d.localToWorld(new THREE.Vector3());
        const nextPosition = portalWorldPos.clone().addScaledVector(portalDirection, 1.1);

        const duration = 2;
        const ease = Power2.easeIn

        const { x, y, z } = nextPosition;

        gsap.to(this.currentWorld.camera.position, {
            x, y, z, duration, ease,
            onUpdate: () => this.currentWorld.camera.lookAt(this.controls.target),
            onComplete: () => this.switchToLibrary(),
        });

        this.uiPanels.mode = PanelMode.TRANSITION;
        this.uiPanels.hideTitle()

    }

    switchToWorld() {
        this.resize();

        const { camera, titleBrightness, titleDisplace, titleScale } = this.currentWorld;

        this.currentCamera = this.currentWorld.camera;
        this.currentWorld.tempTarget = this.controls.target;

        this.setOrbitControls(this.currentWorld);

        this.matchCameras(camera, this.library.camera, 1);
        this.currentWorld.camera.add(this.uiPanels.model);
        this.currentWorld.updateStep({ enterToWorld: true });

        // Update UI panels
        this.uiPanels.mode = PanelMode.WORLD;
        this.uiPanels.showTitle({
            titleId: this.library.currentStep - 1, // Maybe make a more readable var for this - something like titleId, with getTitleId() method in LibraryWorld
            titleBrightness, titleDisplace, titleScale,
            timeout: 5000,
        });

        // Show the poem for the current world
        this.showPoem(this.library.currentStep - 1);

        const { currentPortal } = this.library;
        const audioConfig = {
            name: currentPortal.name,
            audioData: currentPortal.audioData,
            autoPlay: true,
        }

        this.audioEngine.loadAudioForWorld(audioConfig);

        this.currentWorld.isFound = true;

        this.view = AppView.WORLD; // Calls a change in the run loop
    }

    switchToLibrary() {
        this.resize();

        this.currentWorld.animateToPortalTransforms();
        this.currentCamera = this.library.camera;

        if (this.library.view === LibraryView.IN_PORTAL) {
            this.setOrbitControls(this.library)
            this.matchCameras(this.library.camera, this.currentWorld.camera, 1)
            this.library.tempTarget = this.controls.target;

            if (this.library.debug) {
                this.library.camTargetChecker.position.copy(this.controls.target)
            }
        }

        this.library.view = LibraryView.GLOBAL;
        this.library.camera.add(this.uiPanels.model)
        this.uiPanels.mode = PanelMode.LIBRARY;

        // updating markers

        if (this.currentWorld.isFound && !this.currentWorld.isMarked) {

            const marker = this.uiPanels.getMarkerByWorld(this.currentWorld.name)
            this.uiPanels.activateWorld(this.currentWorld.name)

            const trailStart = this.library.currentPortal.portal3d;
            const trailEnd = marker.model
            this.library.trail.launchTrail({
                emitter: trailStart,
                destination: trailEnd
            })

            this.currentWorld.isMarked = true;
        }


        // Load the library audio world if we aren't on it
        if (!this.audioEngine.isLibraryWorld) {
            this.loadLibraryAudio({ autoPlay: true });
        }

        // Show a final poem if all the worlds were discovered
        // 

        let camDistance = 30;

        if (this.allWorldsDiscovered && !this.minimalMode) {
            this.showPoem(PoemId.LIBRARY_END);
            this.library.launchPapers({ delay: 5 });
            camDistance = 45
        } else {
            //*
            this.uiPanels.hidePoem();
            //*/
        }

        this.library.frontCameraStep = this.library.currentStep
        this.library.replaceCameraInFrontOfPortal(this.library.currentStep, true, camDistance)

        this.view = AppView.LIBRARY; // Changes the run loop
    }

    exitOrBack() {
        if (this.uiPanels.infoPanel._isOpen) {
            this.uiPanels.closeInfoPanel();
            return;
        }
        if (this.view === AppView.LIBRARY && this.library.view === LibraryView.FOCUS) {
            this.switchToLibrary()
        } else if (this.view === AppView.WORLD) {
            this.exitCurrentPortal()
        }
    }

    /**
     * Loads the library audio world if we aren't on it
     */
    loadLibraryAudio({ autoPlay = true }) {

        // Different setup for the final poem, but use the same audio directory
        const audioData = this.allWorldsDiscovered ? LibraryEndAudioData : LibraryAudioData;

        this.audioEngine.loadAudioForWorld({
            name: this.allWorldsDiscovered ? "libraryEnd" : "library",
            audioData,
            autoPlay,
        });
    }

    previousStep() {
        if (this._view === AppView.WORLD && this.currentWorld) {
            const stepNow = this.currentWorld.previousStep();
            this.audioEngine.selectScene(stepNow);
            this.uiPanels.hideTitle()
        } else if (this.entered) {
            this.library.frontCameraStep--;
            if (this.library.frontCameraStep < 1) this.library.frontCameraStep = 6;
            this.library.replaceCameraInFrontOfPortal(this.library.frontCameraStep);
        }
    }

    nextStep() {
        if (this._view === AppView.WORLD && this.currentWorld) {
            const stepNow = this.currentWorld.nextStep();
            this.audioEngine.selectScene(stepNow);
            this.uiPanels.hideTitle()
        } else if (this.entered) {
            this.library.frontCameraStep++;
            if (this.library.frontCameraStep > 6) this.library.frontCameraStep = 1;
            this.library.replaceCameraInFrontOfPortal(this.library.frontCameraStep);
        }
    }

    start() {
        this.runLoop = this.libraryLoop;
        this.loop();
    }

    toggleMinimalMode() {
        this.minimalMode = !this.minimalMode;
        this.audioEngine.setMinimalMode(this.minimalMode);
        this.uiPanels.setMinimalMode(this.minimalMode);
    }

    toggleVoiceover() {
        const currentState = this.audioEngine.loadVoiceoverState();
        this.audioEngine.setMinimalMode(!currentState);
    }

    /**
     * Show a specific poem for the world
     * but only if we're not in minimal mode
     * 
     * @param {number} poemId 
     */
    showPoem(poemId) {
        if (this.minimalMode) {
            return;
        }

        this.uiPanels.showPoem({
            poemId,
            screenHeight: this.screenHeight,
            screenWidth: this.screenWidth,
            timeout: poemId === PoemId.LIBRARY_END ? 20000 : 16000,
            delay: 3,
        });
    }

    loop() {
        this.dt = this.clock.getDelta();
        this.time += this.dt;

        const loopParams = {
            time: this.time,
            deltaTime: this.dt,
        }

        this.uiPanels.loop(loopParams);
        this.runLoop(loopParams); // this flips between library loop and world loop

        window.requestAnimationFrame(this.loop);
    }

    /**
     * 
     * @param {time, deltaTime} loopParams 
     * 
     * Runs the library loop
     */
    libraryLoop(loopParams) {
        this.library.loop(loopParams);
        let dirCamera = new THREE.Vector3();

        const { camera, scene } = this.library;
        camera.getWorldDirection(dirCamera);

        this.library.portals.forEach(portal => {
            const { world, collidable } = portal;
            const collidableDot = collidable.worldDirection.dot(dirCamera);

            if (collidableDot < -0.5 && world.allowRender) {
                this.matchCameras(world.camera, camera, 1);
                this.library.screenshotPortal({ portal, renderer: this.renderer });
            }
        });

        this.renderer.render(scene, camera);
    }

    /**
     * 
     * @param {time, deltaTime} loopParams
     * 
     * Runs the current world loop
     */
    worldLoop(loopParams) {
        const { scene, camera } = this.currentWorld;
        this.currentWorld.loop(loopParams)
        this.matchCameras(this.library.camera, camera, 1)
        this.renderer.render(scene, camera)
    }

    matchCameras(camera, target, progress) {
        const { position, quaternion } = target;

        if (progress !== 1) {
            camera.position.lerp(position, progress);
            camera.quaternion.slerp(quaternion, progress);
        } else {
            camera.position.copy(position);
            camera.quaternion.copy(quaternion);
        }
    }

    resize() {
        if (this.currentWorld) {
            this.currentWorld.resize(window.innerWidth, window.innerHeight);
        }

        this.library.resize(window.innerWidth, window.innerHeight);

        this.renderer.setSize(window.innerWidth, window.innerHeight);

        this.screenWidth = this.getVisibleWidthAtDepth(this.uiPanels.model.position.z);
        this.screenHeight = this.getVisibleHeightAtDepth(this.uiPanels.model.position.z);
        this.uiPanels.resize(this.screenWidth, this.screenHeight);
    }

    mouseMove(mousePos) {
        this.mousePos = mousePos;
        this.mouse.x = mousePos.px;
        this.mouse.y = - mousePos.py;
        this.mouseTarget.mouseMove(mousePos);
        this.uiPanels.mouseMove(mousePos)
    }

    getVisibleHeightAtDepth(d) {
        const vFOV = this.currentCamera.fov * Math.PI / 180;
        return 2 * Math.tan(vFOV / 2) * Math.abs(d);
    }

    getVisibleWidthAtDepth(d) {
        const height = this.getVisibleHeightAtDepth(d);
        return height * this.currentCamera.aspect;
    };

    handleMouseDown() {
        this.mouseTarget.mouseDown();
        this.uiPanels.mouseDown(this.mousePos)
    }

    handleMouseUp() {
        this.mouseTarget.mouseUp();
        this.uiPanels.mouseUp(this.mousePos)
    }

    /**
     * 
     * @param {THREE.Object3d} world
     * 
     * Sets the orbit controls for the given world
     */
    setOrbitControls(world) {
        if (!this.controls) {
            this.controls = new OrbitControls(world.camera, this.renderer.domElement);
            this.controls.target = world.tempTarget
            this.controls.minDistance = 0;
            this.controls.maxDistance = 80;
            this.controls.zoomSpeed = 0.5;
            this.controls.maxPolarAngle = Math.PI / 2 + .1;
            this.controls.minPolarAngle = .02;
        } else {
            this.controls.object = world.camera;
        }

        world.setControls(this.controls);
    }

    /**
     * Gets the current world from the library
     */
    get currentWorld() {
        return this.library.currentPortal ? this.library.currentPortal.world : null;
    }

    /**
     * Sets the current view, either the library or the current world
     */
    set view(value) {
        this._view = value;

        // also update the run loop
        this.runLoop = this[`${value}Loop`]; // libraryLoop | worldLoop
    }

    /**
     * Returns the current view, either the library or the current world
     */
    get view() {
        return this._view;
    }

    /**
     * Returns the current mouse target, either the library or the current world
     */
    get mouseTarget() {
        return this._view === AppView.LIBRARY ? this.library : this.currentWorld;
    }

    get allWorldsDiscovered() {
        return this.uiPanels.allMarkersActivated || this._testEnd;
    }

    testEnd() {
        this._testEnd = !this._testEnd;
        this.library.launchPapers();
        this.loadLibraryAudio({ autoPlay: true });
        this.showPoem(PoemId.LIBRARY_END);
    }
}